import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import produce from 'immer';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { ApprovalMethodDateModel } from '../ApprovalMethodDateModel';
import { PriceIncreaseModel } from '../PriceIncreaseModel';
import { IntegratedClassType, ServiceIntegrationsModel } from '../ServiceIntegrations';
import { ServiceStatusType } from './model';
import * as selectors from './selectors';
export function makeChanges(changes) {
    return {
        name: changes.name != null ? changes.name : null,
        description: changes.description != null ? changes.description : null,
        terms: changes.terms != null ? changes.terms : null,
        cost: changes.cost != null ? changes.cost : null,
        billing: changes.billing != null ? changes.billing : null,
        bundle: changes.bundle != null ? changes.bundle : null
    };
}
export function matchStatusType(status, matcher) {
    switch(status.type){
        case ServiceStatusType.AwaitingApproval:
            return matcher.awaitingApproval(status);
        case ServiceStatusType.Amended:
            return matcher.amended(status);
        case ServiceStatusType.Approved:
            return matcher.approved(status);
        case ServiceStatusType.Completed:
            return matcher.completed(status);
        case ServiceStatusType.Terminated:
            return matcher.terminated(status);
        case ServiceStatusType.Canceled:
            return matcher.canceled(status);
    }
}
export const makeRepeatableManualStatus = (service)=>{
    return matchStatusType(service.status, {
        awaitingApproval: (status)=>{
            return _object_spread_props(_object_spread({}, status), {
                awaitingApproval: _object_spread_props(_object_spread({}, status.awaitingApproval), {
                    effectiveDate: null,
                    effectiveDateSource: null
                })
            });
        },
        amended: (status)=>{
            return _object_spread_props(_object_spread({}, status), {
                amended: _object_spread_props(_object_spread({}, status.amended), {
                    effectiveDate: null,
                    effectiveDateSource: null
                })
            });
        },
        approved: (status)=>{
            return _object_spread_props(_object_spread({}, status), {
                approved: _object_spread_props(_object_spread({}, status.approved), {
                    effectiveDate: null,
                    effectiveDateSource: null
                })
            });
        },
        completed: (status)=>{
            return _object_spread_props(_object_spread({}, status), {
                completed: _object_spread_props(_object_spread({}, status.completed), {
                    effectiveDate: null,
                    effectiveDateSource: null
                })
            });
        },
        terminated: (status)=>{
            return status;
        },
        canceled: (status)=>{
            return status;
        }
    });
};
export const makeOneTimeAutomaticStatus = makeRepeatableManualStatus;
export const makeOneTimeManualStatus = makeRepeatableManualStatus;
export const makeOnApprovalStatus = makeRepeatableManualStatus;
export const makeOneTimeInStagesStatus = makeRepeatableManualStatus;
export function makeAwaitingApprovalStatus() {
    let options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    var _options_effectiveDate, _options_effectiveDateSource;
    return {
        type: ServiceStatusType.AwaitingApproval,
        awaitingApproval: {
            addedOn: options.addedOn || new Date(),
            approvalMethod: options.approvalMethod || ApprovalMethodDateModel.makeManual(),
            note: options.note || null,
            limitedTimeDiscountOffer: options.limitedTimeDiscountOffer,
            effectiveDate: (_options_effectiveDate = options.effectiveDate) !== null && _options_effectiveDate !== void 0 ? _options_effectiveDate : null,
            effectiveDateSource: (_options_effectiveDateSource = options.effectiveDateSource) !== null && _options_effectiveDateSource !== void 0 ? _options_effectiveDateSource : null
        }
    };
}
export function makeAmendedStatus() {
    let options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    var _options_effectiveDate, _options_effectiveDateSource;
    return {
        type: ServiceStatusType.Amended,
        date: options.date || new Date(),
        amended: {
            amendedOn: options.amendedOn || new Date(),
            changes: makeChanges(options.changes || {}),
            approvalMethod: options.approvalMethod || ApprovalMethodDateModel.makeManual(),
            note: options.note || null,
            effectiveDate: (_options_effectiveDate = options.effectiveDate) !== null && _options_effectiveDate !== void 0 ? _options_effectiveDate : null,
            effectiveDateSource: (_options_effectiveDateSource = options.effectiveDateSource) !== null && _options_effectiveDateSource !== void 0 ? _options_effectiveDateSource : null
        }
    };
}
export function makeApprovedStatus(param) {
    let { date, effectiveDate, effectiveDateSource } = param;
    return {
        type: ServiceStatusType.Approved,
        date,
        approved: {
            effectiveDate: effectiveDate !== null && effectiveDate !== void 0 ? effectiveDate : null,
            effectiveDateSource: effectiveDateSource !== null && effectiveDateSource !== void 0 ? effectiveDateSource : null
        }
    };
}
export function makeCompletedStatus(date, changes) {
    let options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
    const { effectiveDate, effectiveDateSource } = options;
    const status = {
        type: ServiceStatusType.Completed,
        date,
        completed: {
            effectiveDate: effectiveDate !== null && effectiveDate !== void 0 ? effectiveDate : null,
            effectiveDateSource: effectiveDateSource !== null && effectiveDateSource !== void 0 ? effectiveDateSource : null
        }
    };
    if (changes) {
        status.completed.changes = makeChanges(changes);
    }
    return status;
}
export function makeTerminatedStatus(date) {
    return {
        type: ServiceStatusType.Terminated,
        date
    };
}
export function makeCanceledStatus() {
    return {
        type: ServiceStatusType.Canceled
    };
}
export function fromFields(_0) {
    let { bundle, taxable } = _0, common = _object_without_properties(_0, [
        "bundle",
        "taxable"
    ]);
    return bundle ? _object_spread_props(_object_spread({}, common), {
        bundle
    }) : _object_spread_props(_object_spread({}, common), {
        taxable
    });
}
export function makeService(input) {
    var _input_id, _input_terms, _input_isBillable, _input_participation;
    let service = fromFields({
        id: (_input_id = input.id) !== null && _input_id !== void 0 ? _input_id : generateUniqueId('service'),
        name: input.name,
        description: input.description,
        terms: (_input_terms = input.terms) !== null && _input_terms !== void 0 ? _input_terms : '',
        status: input.status,
        cost: input.cost,
        billing: input.billing,
        isBillable: (_input_isBillable = input.isBillable) !== null && _input_isBillable !== void 0 ? _input_isBillable : false,
        serviceTemplateId: input.serviceTemplateId,
        participation: (_input_participation = input.participation) !== null && _input_participation !== void 0 ? _input_participation : makeMandatoryParticipation(),
        integrations: input.integrations || ServiceIntegrationsModel.fromDTO(undefined),
        source: input.source,
        bundle: input.bundle,
        taxable: input.taxable,
        taxStatus: input.taxStatus
    });
    if (!selectors.supportsPriceIncrease(service)) {
        service = produce(service, (s)=>{
            s.cost.priceIncrease = PriceIncreaseModel.makeNone();
        });
    }
    return service;
}
export function makeMandatoryParticipation() {
    return {
        type: 'mandatory'
    };
}
export function makeOptionalParticipation(includedByDefault) {
    return {
        type: 'optional',
        optional: {
            includedByDefault
        }
    };
}
export function makeIntegratedClassUnset() {
    return {
        type: IntegratedClassType.Unset
    };
}
export function makeIntegratedClassSet(id) {
    return {
        type: IntegratedClassType.Set,
        set: {
            id
        }
    };
}
